import * as React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { KanbanCard } from './kanban-card.js';
import type { KanbanCase } from '../lib/cases-api.js';
import type { CaseCostsSummaryItem } from '../lib/costs-api.js';
import type { CaseDisplayName } from '../lib/case-title.js';
import type { CoverInfo } from '../lib/case-rows.js';
import type { PropertyListItem } from '../lib/properties.js';
import { kindLabel } from '../lib/properties.js';
import { stageDotClassA2, stageLabel } from '../lib/case-stages.js';
import { useT } from '../lib/i18n.js';

/**
 * Krok TAB1 bod 8 — kolik karet sloupec ukaze, nez nabidne „+ N dalších".
 * Predloha `docs/design/ux-v3/tabule/ZakazkyKanban.png` ma ve sloupci nejvys
 * dve az tri karty a pod nimi radek „+ 52 dalších". Bez toho merí sloupec
 * „Inzerce" (54 zakazek) nekolik obrazovek a kanban prestava byt prehledem.
 */
export const KANBAN_VISIBLE_CARDS = 3;

export interface KanbanColumnProps {
  readonly stage: string;
  readonly items: readonly KanbanCase[];
  readonly ownerName: (userId: string | null) => string | null;
  /** O8: nazev k zobrazeni — interni id se na kartu nikdy nedostane (lib/case-title.ts). */
  readonly displayName: (item: KanbanCase) => CaseDisplayName;
  readonly now: Date;
  readonly onOpen: (id: string) => void;
  /** undefined = zadny presun neprobiha; jinak true/false podle allowedTransitions tazene karty. */
  readonly dropAllowed: boolean | undefined;
  /** Hromadny souhrn nakladu/marze per zakazka (O6a, polozka 3) — nacteny jednim requestem v zakazky.tsx. */
  readonly costsByCase: ReadonlyMap<string, CaseCostsSummaryItem>;
  /** O6: titulni fotky per nemovitost, take jednim requestem (viz zakazky.tsx). */
  readonly coverByProperty: ReadonlyMap<string, CoverInfo>;
  /** Krok TAB1 bod 9: druh nemovitosti a obec do radku pod adresou. */
  readonly briefByProperty: ReadonlyMap<string, PropertyListItem>;
  /** Krok TAB1 bod 9: „co zakazku brzdi" z ukolu makleře (`crm_tasks`). */
  readonly nextStepByCaseId: ReadonlyMap<string, string>;
}

/**
 * Sloupec kanbanu = jedna stage stavoveho automatu. Drop zonu nabizi
 * @dnd-kit vzdy (jinak by se karta nedala pustit), ale VIZUALNI signal
 * (zeleny/sedy ram) a skutecne prijeti v `onDragEnd` (viz zakazky.tsx) se
 * ridi `allowedTransitions` z API - nepovoleny cil tak neni "nabidnuty".
 */
export function KanbanColumn({
  stage,
  items,
  ownerName,
  displayName,
  now,
  onOpen,
  dropAllowed,
  costsByCase,
  coverByProperty,
  briefByProperty,
  nextStepByCaseId,
}: KanbanColumnProps): React.JSX.Element {
  const t = useT();
  const [expanded, setExpanded] = React.useState(false);
  const { setNodeRef } = useDroppable({ id: stage });
  // Krok UXR1: sazba sloupce prevzata z mockupu `02-zakazky.html`
  // (`.kcol` / `.kcol__head` s tecku faze a poctem vpravo); zvyrazneni
  // drop zony zustava z R4 — staticky mockup ho nezna.
  const bodyClass =
    dropAllowed === undefined
      ? 'rh-a2-kcol__body'
      : `rh-a2-kcol__body ${dropAllowed ? 'rh-kanban-column__body--drop-ok' : 'rh-kanban-column__body--drop-no'}`;

  /*
   * Pri PRESUNU se sloupec rozbaluje vzdy: do sbaleneho zbytku by karta
   * spadla „nekam", kde ji uzivatel neuvidi. `dropAllowed !== undefined`
   * znamena, ze prave nekdo nekterou kartu drzi.
   */
  const dragging = dropAllowed !== undefined;
  const visible = expanded || dragging ? items : items.slice(0, KANBAN_VISIBLE_CARDS);
  const hidden = items.length - visible.length;

  return (
    <div className="rh-a2-kcol" data-testid={`kanban-column-${stage}`}>
      <div className="rh-a2-kcol__head">
        <span className={`rh-a2-dot ${stageDotClassA2(stage)}`} aria-hidden="true" />
        <span>{stageLabel(stage)}</span>
        {/*
          Krok TAB1 bod 7 — ROZHODNUTI TAB1-POCTY: cislo ve sloupci je pocet
          zakazek PO AKTUALNIM FILTRU, ne celkem. Predloha ukazuje „3 tvoje" a
          zaroven 100 ve sloupcich, coz je vada mockupu (zadani ji pojmenovava);
          sloupec, ktery ukazuje tri karty a tvrdi 54, by lhal.
        */}
        <span className="rh-a2-count" data-testid={`kanban-column-count-${stage}`}>
          {items.length}
        </span>
      </div>
      <div ref={setNodeRef} className={bodyClass}>
        {visible.map((item) => {
          const cover = item.propertyId === null ? undefined : coverByProperty.get(item.propertyId);
          const brief = item.propertyId === null ? undefined : briefByProperty.get(item.propertyId);
          return (
            <KanbanCard
              key={item.id}
              item={item}
              ownerName={ownerName(item.ownerUserId)}
              displayName={displayName(item)}
              now={now}
              onOpen={onOpen}
              costsSummary={costsByCase.get(item.id)}
              coverUrl={cover?.thumbUrl ?? null}
              lat={cover?.lat ?? null}
              lon={cover?.lon ?? null}
              propertyId={item.propertyId}
              propertyKindLabel={brief === undefined ? null : kindLabel(brief.kind)}
              locality={brief?.locality ?? null}
              crmNextStep={nextStepByCaseId.get(item.id) ?? null}
            />
          );
        })}
        {hidden > 0 ? (
          <button
            type="button"
            className="rh-a2-kcol__more"
            data-testid={`kanban-column-more-${stage}`}
            onClick={() => setExpanded(true)}
          >
            {t('webAdmin.kanban.moreCards', { count: String(hidden) })}
          </button>
        ) : null}
        {expanded && items.length > KANBAN_VISIBLE_CARDS ? (
          <button
            type="button"
            className="rh-a2-kcol__more"
            data-testid={`kanban-column-less-${stage}`}
            onClick={() => setExpanded(false)}
          >
            {t('webAdmin.kanban.fewerCards')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
